import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const SCROLL_Y_KEY = 'rp:home-scroll-y';
const SCROLL_SECTION_KEY = 'rp:home-return-section';

let savedHome = { y: 0, section: '' };
let suppressScrollSave = false;
let restoreUntil = 0;
let pendingHomeTop = false;

/** Logo / home-brand click: land at the top of the homepage, not the last section. */
export function requestHomeTop({ immediate = false } = {}) {
  savedHome = { y: 0, section: '' };
  freezeSaves(1200);
  try {
    sessionStorage.removeItem(SCROLL_Y_KEY);
    sessionStorage.removeItem(SCROLL_SECTION_KEY);
  } catch {
    /* ignore */
  }
  if (immediate) {
    pendingHomeTop = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    return;
  }
  pendingHomeTop = true;
}

function freezeSaves(ms = 1000) {
  suppressScrollSave = true;
  restoreUntil = Date.now() + ms;
  window.setTimeout(() => {
    if (Date.now() >= restoreUntil) suppressScrollSave = false;
  }, ms + 50);
}

function scrollWindowTo(y) {
  const top = Math.max(0, Number(y) || 0);
  freezeSaves(800);
  window.scrollTo(0, top);
  document.documentElement.scrollTop = top;
  document.body.scrollTop = top;
}

function persistHome(y, sectionId) {
  const nextY = Math.max(0, Number(y) || 0);
  const nextSection = sectionId || savedHome.section || '';
  savedHome = { y: nextY || savedHome.y, section: nextSection };
  try {
    if (savedHome.y > 0) sessionStorage.setItem(SCROLL_Y_KEY, String(savedHome.y));
    if (savedHome.section) sessionStorage.setItem(SCROLL_SECTION_KEY, savedHome.section);
  } catch {
    /* ignore */
  }
}

function readSavedHome() {
  try {
    const y = Number(sessionStorage.getItem(SCROLL_Y_KEY));
    const section = sessionStorage.getItem(SCROLL_SECTION_KEY) || '';
    if (Number.isFinite(y) && y > 0) savedHome.y = Math.max(savedHome.y, y);
    if (section) savedHome.section = section;
  } catch {
    /* ignore */
  }
  return savedHome;
}

/** Call from product links so Back returns to that home section. */
export function rememberHomeScroll(sectionId) {
  const y = window.scrollY || window.pageYOffset || 0;
  persistHome(y, sectionId);
}

function restoreHomePosition() {
  readSavedHome();
  const { y, section } = savedHome;

  if (section) {
    const el = document.getElementById(section);
    if (el) {
      freezeSaves(1200);
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
      return window.scrollY || y;
    }
  }

  if (y > 0) {
    scrollWindowTo(y);
    return y;
  }

  return 0;
}

export default function ScrollToTop() {
  const { pathname, hash, key } = useLocation();
  const navigationType = useNavigationType();
  const prevPathRef = useRef(pathname);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    readSavedHome();
  }, []);

  useEffect(() => {
    if (pathname !== '/') return undefined;

    const save = () => {
      if (suppressScrollSave || Date.now() < restoreUntil) return;
      const y = window.scrollY || window.pageYOffset || 0;
      if (y <= 0) return;
      persistHome(y, savedHome.section);
    };

    window.addEventListener('scroll', save, { passive: true });
    return () => window.removeEventListener('scroll', save);
  }, [key, pathname]);

  useLayoutEffect(() => {
    const prevPath = prevPathRef.current;
    prevPathRef.current = pathname;

    if (pathname === '/' && pendingHomeTop) {
      pendingHomeTop = false;
      scrollWindowTo(0);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return undefined;
    }

    // Back from a product (browser Back, in-page ← Back, or link home)
    // → put the user on the section they left
    if (pathname === '/' && prevPath.startsWith('/products/')) {
      restoreHomePosition();
      const retry = () => restoreHomePosition();
      const t1 = requestAnimationFrame(retry);
      const t2 = setTimeout(retry, 50);
      const t3 = setTimeout(retry, 200);
      const t4 = setTimeout(retry, 500);
      return () => {
        cancelAnimationFrame(t1);
        clearTimeout(t2);
        clearTimeout(t3);
        clearTimeout(t4);
      };
    }

    // Nav hash links (Shop All, For Him, …)
    if (hash && navigationType !== 'POP') {
      const id = hash.replace('#', '');
      const scrollToHash = () => {
        const el = document.getElementById(id);
        if (!el) return false;
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
      };
      if (scrollToHash()) return undefined;
      const t1 = setTimeout(scrollToHash, 50);
      const t2 = setTimeout(scrollToHash, 250);
      const t3 = setTimeout(() => {
        if (!scrollToHash()) scrollWindowTo(0);
      }, 500);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
      };
    }

    // Forward navigation to product (or any non-home route): top of page
    if (navigationType !== 'POP' && pathname !== '/') {
      scrollWindowTo(0);
    }

    return undefined;
  }, [pathname, hash, key, navigationType]);

  return null;
}
